import { useCallback, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

// The left rail, arranged the way each person likes it (§09a).
//
// Three things a person can change, all remembered per login on this device:
//
//   collapse   the whole rail folds to its icons — more room for a forty-line
//              bill — and opens again from the same button
//   close      each section heading opens and closes its own group; the page
//              you are on stays visible even inside a closed group, so the
//              rail never hides where you are
//   arrange    sections move up and down, so a shop that lives in Billing can
//              put Transactions first
//
// Kept in localStorage, not on the server: it is a preference about this
// screen on this machine, the same footing as the theme toggle. Every read and
// write is guarded — a private window or blocked storage just means the
// default rail, never a broken one. The rail also scrolls on its own, so the
// bottom items are reachable on a short screen without scrolling the page.

const STORE = 'tiles-pos:nav'

function readPrefs(userId) {
  try {
    const raw = window.localStorage.getItem(`${STORE}:${userId}`)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writePrefs(userId, prefs) {
  try {
    window.localStorage.setItem(`${STORE}:${userId}`, JSON.stringify(prefs))
  } catch {
    /* storage blocked: the rail still works, it just forgets on reload */
  }
}

/** `[prefs, update]` for this login. `update` takes a patch or a function. */
export function useNavPrefs(userId) {
  const who = userId || 'anonymous'
  const [prefs, setPrefs] = useState(() => readPrefs(who))
  useEffect(() => setPrefs(readPrefs(who)), [who])
  const update = useCallback(
    (patch) =>
      setPrefs((current) => {
        const next = { ...current, ...(typeof patch === 'function' ? patch(current) : patch) }
        writePrefs(who, next)
        return next
      }),
    [who],
  )
  return [prefs, update]
}

/** Whether the strip layout is on (the rail turns horizontal under 1080px),
 *  where collapsing and closing groups would only hide chips. */
function useNarrow() {
  const query = '(max-width: 1079px)'
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(query).matches,
  )
  useEffect(() => {
    const list = window.matchMedia?.(query)
    if (!list) return undefined
    const onChange = () => setNarrow(list.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [])
  return Boolean(narrow)
}

/** NavLink's own rule, for deciding what stays visible in a closed group. */
function isCurrent(item, pathname) {
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

/** Saved order first, then any section added since, in its default place. */
function inOrder(sections, saved) {
  const order = Array.isArray(saved) ? saved : []
  const known = order
    .map((id) => sections.find((section) => section.id === id))
    .filter(Boolean)
  return [...known, ...sections.filter((section) => !order.includes(section.id))]
}

export default function SideNav({ sections, isVisible, prefs, onPrefs }) {
  const { pathname } = useLocation()
  const narrow = useNarrow()
  const [arranging, setArranging] = useState(false)

  const collapsed = Boolean(prefs.collapsed) && !narrow
  const closed = Array.isArray(prefs.closed) ? prefs.closed : []

  // Only sections with something this person may open take part — a heading
  // with nothing under it renders nothing, and is not something to move.
  const shown = inOrder(sections, prefs.order)
    .map((section) => ({ ...section, items: section.items.filter(isVisible) }))
    .filter((section) => section.items.length > 0)

  function toggleGroup(id) {
    onPrefs((current) => {
      const now = Array.isArray(current.closed) ? current.closed : []
      return { closed: now.includes(id) ? now.filter((x) => x !== id) : [...now, id] }
    })
  }

  function move(id, by) {
    const visibleIds = shown.map((section) => section.id)
    const at = visibleIds.indexOf(id)
    const to = at + by
    if (at < 0 || to < 0 || to >= visibleIds.length) return
    const full = inOrder(sections, prefs.order).map((section) => section.id)
    const a = full.indexOf(visibleIds[at])
    const b = full.indexOf(visibleIds[to])
    ;[full[a], full[b]] = [full[b], full[a]]
    onPrefs({ order: full })
  }

  return (
    <nav className={collapsed ? 'shell-nav is-collapsed' : 'shell-nav'} aria-label="Main">
      <div className="shell-nav-tools">
        {!collapsed ? (
          <>
            {arranging ? (
              <button
                type="button"
                className="shell-nav-tool"
                onClick={() => onPrefs({ order: [], closed: [] })}
              >
                Reset
              </button>
            ) : null}
            <button
              type="button"
              className={arranging ? 'shell-nav-tool is-on' : 'shell-nav-tool'}
              aria-pressed={arranging}
              onClick={() => setArranging((now) => !now)}
            >
              {arranging ? 'Done' : 'Arrange'}
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="shell-nav-tool shell-nav-fold"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Open the menu' : 'Close the menu to icons'}
          title={collapsed ? 'Open the menu' : 'Close the menu to icons'}
          onClick={() => {
            setArranging(false)
            onPrefs({ collapsed: !collapsed })
          }}
        >
          <FoldIcon open={!collapsed} />
        </button>
      </div>

      <ul>
        {shown.flatMap((section, index) => {
          const label = section.heading ?? (arranging ? section.arrangeLabel : null)
          const isClosed = !collapsed && !arranging && !narrow && closed.includes(section.id)
          const items = isClosed
            ? section.items.filter((item) => isCurrent(item, pathname))
            : section.items

          const heading =
            label && !collapsed ? (
              <li key={`${section.id}-heading`} className="shell-section-label">
                {arranging ? (
                  <span className="shell-section-arrange">
                    <span>{label}</span>
                    <span className="shell-section-moves">
                      <button
                        type="button"
                        className="shell-section-move"
                        aria-label={`Move ${label} up`}
                        disabled={index === 0}
                        onClick={() => move(section.id, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="shell-section-move"
                        aria-label={`Move ${label} down`}
                        disabled={index === shown.length - 1}
                        onClick={() => move(section.id, 1)}
                      >
                        ↓
                      </button>
                    </span>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="shell-section-toggle"
                    aria-expanded={!isClosed}
                    onClick={() => toggleGroup(section.id)}
                  >
                    <span>{label}</span>
                    <ChevronIcon open={!isClosed} />
                  </button>
                )}
              </li>
            ) : null

          return [
            heading,
            ...items.map((item, itemIndex) => (
              <li
                // Section id as well as the path: /receipts is listed twice
                // (Transactions and Payments), and this is one flat list.
                key={`${section.id}:${item.to}`}
                // A group with no label on screen carries the lead-in space on
                // its own first item, or it reads as more of the group above.
                className={
                  !heading && itemIndex === 0 && index > 0 ? 'shell-section-start' : undefined
                }
              >
                <NavLink
                  to={item.to}
                  end={item.end}
                  title={collapsed ? item.label : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  className={({ isActive }) => (isActive ? 'shell-link is-active' : 'shell-link')}
                >
                  <item.icon />
                  {collapsed ? null : <span className="shell-link-text">{item.label}</span>}
                </NavLink>
              </li>
            )),
          ].filter(Boolean)
        })}
      </ul>
    </nav>
  )
}

function FoldIcon({ open }) {
  return (
    <svg className="nav-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="1.6" y="2.4" width="12.8" height="11.2" rx="1.6" />
      <path d="M6 2.4v11.2" />
      <path d={open ? 'M11 6L9 8l2 2' : 'M9 6l2 2-2 2'} />
    </svg>
  )
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={open ? 'shell-chevron' : 'shell-chevron is-closed'}
      width="12"
      height="12"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  )
}
